import React, { createContext, useContext, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { getImage, getSrc } from 'gatsby-plugin-image';
import useSite from '../hooks/use-site';

const Context = createContext();

export const useMeta = () => useContext(Context);

const MetaProvider = ({ title, children }) => {
  const value = useMemo(() => ({
    title,
  }), [
    title,
  ]);
  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

const titleTemplate = (titleSeparator, title) => {
  if (!title) {
    return null;
  }
  const seperator = titleSeparator.length ? titleSeparator : '-';

  return `${seperator} ${title}`;
};

const Meta = ({
  headPageTitle = null,
  headMetaTitle = null,
  headTitle,
  headSlug,
  headMetaDescription,
  headImage,
  children,
}) => {
  const { metadata } = useStaticQuery(graphql`
    query SiteTitleQuery {
      metadata: site {
        siteMetadata {
          title
          siteUrl
        }
      }
    }
  `);
  const {
    description: siteDescription,
    title: siteTitle,
    titleSeparator,
    fixedDefaultImage,
  } = useSite();
  const description = headMetaDescription || siteDescription || null;
  const { siteUrl } = metadata.siteMetadata;
  const canonical = headSlug ? `${siteUrl}${headSlug.endsWith('/') ? headSlug : `${headSlug}/`}` : undefined;

  const activeImage = headImage || fixedDefaultImage || null;
  const metaImage = getImage(activeImage?.gatsbyImage || activeImage?.localFile) ?? activeImage;
  const metaImageSrc = getSrc(metaImage) ? `${siteUrl}${getSrc(metaImage)}` : (metaImage?.src || metaImage);

  const pageTitle = headPageTitle || headMetaTitle || headTitle;

  return (
    <MetaProvider title={pageTitle}>
      <Helmet
        titleTemplate={headPageTitle ? null : `%s ${titleTemplate(titleSeparator, siteTitle) || ''}`.trim()}
      >
        <html lang="nl" />
        <meta name="robots" content="index, follow" />
        <meta property="og:site_name" content={siteTitle} />
        <meta property="og:title" content={headMetaTitle || headTitle} />
        <meta property="og:locale" content="nl_NL" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <link rel="alternate" hrefLang="x-default" href={canonical} />
        <link rel="canonical" href={canonical} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={headMetaTitle || headTitle} />

        {metaImageSrc && <meta name="twitter:image" content={metaImageSrc} />}
        {metaImageSrc && <meta property="og:image" content={metaImageSrc} />}
        {metaImage?.width && <meta property="og:image:width" content={metaImage.width} />}
        {metaImage?.height && <meta property="og:image:height" content={metaImage.height} />}

        {description && <meta name="description" content={description} />}
        {description && <meta property="og:description" content={description} />}
        {description && <meta property="twitter:description" content={description} />}

        {/* {site?.searchConsole && (
          <meta name="google-site-verification" content={site?.searchConsole} />
        )} */}

        <title>{pageTitle}</title>
      </Helmet>
      {children}
    </MetaProvider>
  );
};

export default Meta;
